import clsx from 'clsx';
import type React from 'react';

import type { BadgeProps } from './Badge';
import styles from './Badge.module.css';

type UseBadgePropsReturn = {
    badgeProps: React.HTMLAttributes<HTMLElement>;
    isIconOnly: boolean;
    isSingleChar: boolean;
    isEmpty: boolean;
    isFixedSize: boolean;
    isNumber: boolean;
    /** The size for the icon in the badge */
    iconSize: number;
};

export function useBadge(props: BadgeProps): UseBadgePropsReturn {
    const {
        icon,
        iconOnly = false,
        inverted = false,
        kind = 'primary',
        label,
        size = 'small',
        className,
        thin,
        blend = false,
    } = props;
    const isIconOnly = Boolean(icon) && iconOnly;
    const isSingleChar = `${label}`.length === 1;
    const isEmpty = `${label}`.length === 0;
    const isNumber = typeof label === 'number';

    const isFixedSize = isEmpty || isIconOnly || isSingleChar;

    const iconSize = getBadgeIconSize(props);
    const isFeatureAvailability = kind === 'new' || kind === 'alpha' || kind === 'beta';

    return {
        badgeProps: {
            className: clsx(
                'flex shrink-0 items-center justify-center text-center align-middle transition-all duration-200',
                isFixedSize ? styles.fixedSize : 'w-fit min-w-fit',
                thin
                    ? 'gap-0.5 rounded-sm'
                    : isFeatureAvailability
                      ? 'gap-1 rounded-sm'
                      : 'gap-1 rounded-full',
                styles.badge,
                styles[kind],
                styles[size],
                {
                    [styles.inverted]: inverted,
                    [styles.thin]: thin || isFeatureAvailability,
                    [styles.blend]: blend,
                },
                className
            ),
            'aria-label': isIconOnly ? String(label) : undefined,
        },
        isIconOnly,
        isSingleChar,
        isEmpty,
        isFixedSize,
        isNumber,
        iconSize,
    };
}

export function getBadgeIconSize({ size = 'small', thin }: BadgeProps): number {
    if (thin) {
        switch (size) {
            case 'xxsmall':
            case 'xsmall':
                return 8;
            case 'small':
                return 10;
            case 'medium':
                return 12;
        }
    }
    switch (size) {
        case 'xxsmall':
            return 8;
        case 'xsmall':
            return 12;
        default:
            return 14;
    }
}
